import http from '@/api/http'
import store from '@/config/store'

class OrderReminderService {
  constructor() {
    this.intervalId = null
    this.lastOrderId = null
    this.isChecking = false
  }

  /**
   * 开始订单提醒
   */
  startReminder() {
    // 先获取当前最新订单作为基准
    this.getLatestOrder().then(latestOrder => {
      if (latestOrder && latestOrder.uuid) {
        store.commit('setLastOrderId', latestOrder.uuid)
        this.lastOrderId = latestOrder.uuid
      }
      
      // 设置30秒轮询
      this.intervalId = setInterval(() => {
        this.checkNewOrders()
      }, 30000)
      
      store.commit('setReminderIntervalId', this.intervalId)
      console.log('订单提醒服务已启动')
    }).catch(error => {
      console.error('启动订单提醒服务失败:', error)
    })
  }

  /**
   * 停止订单提醒
   */
  stopReminder() {
    if (this.intervalId) {
      clearInterval(this.intervalId)
      this.intervalId = null
      store.commit('setReminderIntervalId', null)
      store.commit('setLastOrderId', null)
      this.lastOrderId = null
      console.log('订单提醒服务已停止')
    }
  }

  /**
   * 检查新订单
   */
  async checkNewOrders() {
    if (this.isChecking) return
    
    this.isChecking = true
    try {
      const latestOrder = await this.getLatestOrder()
      if (!latestOrder || !latestOrder.uuid) return
      
      // 如果发现新订单
      if (this.lastOrderId && latestOrder.uuid !== this.lastOrderId) {
        console.log('发现新订单，触发语音提醒')
        this.playReminderSound()
        
        // 更新最新订单ID
        store.commit('setLastOrderId', latestOrder.uuid)
        this.lastOrderId = latestOrder.uuid
      }
    } catch (error) {
      console.error('检查新订单失败:', error)
    } finally {
      this.isChecking = false
    }
  }

  /**
   * 获取最新订单
   */
  async getLatestOrder() {
    try {
      const response = await http.request('orderList', {
        filter: {
          pay_status: '1' // 只获取已支付的订单
        },
        page: 1
      })
      
      if (response && response.data && response.data.length > 0) {
        // 过滤出已支付的订单
        const paidOrders = response.data.filter(order => order.pay_status === 1)
        if (paidOrders.length > 0) {
          return paidOrders[0] // 返回最新已支付订单
        }
      }
      return null
    } catch (error) {
      console.error('获取最新订单失败:', error)
      throw error
    }
  }

  /**
   * 播放提醒声音
   */
  playReminderSound() {
    // 使用Web Speech API进行语音播报，连续提醒三次，每次间隔3秒
    if ('speechSynthesis' in window) {
      this.playSpeechReminder(3) // 播放三次语音提醒
    } else {
      // 如果浏览器不支持语音合成，播放提示音
      this.playNotificationSound()
    }
  }

  /**
   * 播放语音提醒（多次）
   */
  playSpeechReminder(times) {
    if (times <= 0) return
    
    const utterance = new SpeechSynthesisUtterance('有新的订单，请及时处理')
    utterance.lang = 'zh-CN'
    utterance.rate = 1.0
    utterance.pitch = 1.0
    utterance.volume = 1.0
    
    speechSynthesis.speak(utterance)
    
    // 设置下一次提醒
    if (times > 1) {
      setTimeout(() => {
        this.playSpeechReminder(times - 1)
      }, 3000) // 间隔3秒
    }
  }

  /**
   * 播放提示音
   */
  playNotificationSound() {
    try {
      // 创建音频上下文
      const audioContext = new (window.AudioContext || window.webkitAudioContext)()
      const oscillator = audioContext.createOscillator()
      const gainNode = audioContext.createGain()
      
      oscillator.connect(gainNode)
      gainNode.connect(audioContext.destination)
      
      oscillator.frequency.value = 800
      oscillator.type = 'sine'
      
      gainNode.gain.setValueAtTime(0, audioContext.currentTime)
      gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.1)
      gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.5)
      
      oscillator.start(audioContext.currentTime)
      oscillator.stop(audioContext.currentTime + 0.5)
      
      // 播放两次提示音
      setTimeout(() => {
        const oscillator2 = audioContext.createOscillator()
        const gainNode2 = audioContext.createGain()
        
        oscillator2.connect(gainNode2)
        gainNode2.connect(audioContext.destination)
        
        oscillator2.frequency.value = 600
        oscillator2.type = 'sine'
        
        gainNode2.gain.setValueAtTime(0, audioContext.currentTime)
        gainNode2.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.1)
        gainNode2.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.5)
        
        oscillator2.start(audioContext.currentTime)
        oscillator2.stop(audioContext.currentTime + 0.5)
      }, 600)
    } catch (error) {
      console.error('播放提示音失败:', error)
      // 如果音频API也不支持，显示浏览器通知
      this.showBrowserNotification()
    }
  }

  /**
   * 显示浏览器通知
   */
  showBrowserNotification() {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('新订单提醒', {
        body: '有新的订单，请及时处理',
        icon: '/favicon.ico'
      })
    } else if ('Notification' in window && Notification.permission !== 'denied') {
      Notification.requestPermission().then(permission => {
        if (permission === 'granted') {
          new Notification('新订单提醒', {
            body: '有新的订单，请及时处理',
            icon: '/favicon.ico'
          })
        }
      })
    }
  }
}

// 创建单例实例
const orderReminderService = new OrderReminderService()

export default orderReminderService


// WEBPACK FOOTER //
// ./src/services/orderReminderService.js